/**
 * Horizontal box-plot ranking on the Scenario Results page.
 *
 * Two cohorts shown side by side:
 *   - All projects in the scenario
 *   - Same fuel type as the current project
 *
 * A "Related projects only" toggle further filters each cohort to the
 * region's notion of "related" projects (sourced from
 * `scenarioResults.relatedProjectIds`, which is built per-region in
 * `scenarioResultType.tsx` — SharedAllocations for MISO / NYISO, or
 * SharedMonitoredFacilities for PJM). This keeps the chart cohort
 * consistent with `ScenarioProjectsTable` and friends.
 *
 * Each row shows the 5-number summary (min, P25, median, P75, max) and a
 * dot per project. The current project is a solid blue dot. When the
 * cohort has a huge outlier the X axis is clamped at the cohort's P95 so
 * the IQR box stays readable; outliers are drawn pinned at the right
 * edge with a small "↗" marker and counted in the row sublabel.
 */
import { H4, SegmentedControl, Tag } from "@blueprintjs/core";
import {
    segmentTrackChangeRankingChartCohort,
    segmentTrackChangeRankingChartMetric
} from "in_queue/analytics";
import {
    useAllQueueProjects,
    useSpeculativeProjects,
    useSubscriptionForCluster,
    useTrackedProjectsInCluster
} from "in_queue/contexts/ProjectDataContext";
import {
    useCurrentScenarioMetadata,
    useCurrentTrackedProject
} from "in_queue/contexts/ScenarioPageContext";
import { useScenarioResults } from "in_queue/contexts/ScenarioResultsContext";
import { ScenarioProject } from "in_queue/types/scenarioResultType";
import { fiveNumberSummary } from "in_queue/utils/fiveNumberSummary";
import { formatCosts, formatDollarsPerMw } from "in_queue/utils/formatting";
import { formatRank } from "in_queue/utils/rankFormatting";
import { useMemo, useRef, useState } from "react";
import { ChartMetricSelect } from "./ChartMetricSelect";
import chartsCss from "./charts.module.scss";
import {
    buildRankingDots,
    RankingCohort,
    RankingCostMetric,
    RankingDot
} from "./rankingDots";
import css from "./ScenarioRankingChart.module.scss";

/**
 * No props — the chart derives its full per-scenario project pool from
 * `ScenarioResultsContext.projects`, whose rows carry the pipeline's
 * rank alongside the costs. The
 * results context's `projects` field already includes every project
 * in the scenario regardless of subscription
 * (`getScenarioResultsByProjectId` fetches `scenario_projects` with no
 * project_id filter), so the box plot's "All projects" view stays full-
 * pool. The privacy filter for limited-projectLimit subscriptions runs
 * inside the hook against an allow-list of tracked + speculative +
 * related projects.
 */
export const ScenarioRankingChart: React.FC = () => {
    const [metric, setMetric] = useState<RankingCostMetric>("perMw");
    // "Cohort" picker (Related projects | All projects). Limited-
    // projectLimit subscriptions are pinned to "RELATED" — they don't
    // have access to other projects' results — so we hide the picker
    // and force the value below.
    const [cohort, setCohort] = useState<RankingCohort>("ALL");
    const currentProject = useCurrentTrackedProject();
    const { assumptions } = useCurrentScenarioMetadata();
    const queueProjectsL = useAllQueueProjects();
    const speculativeProjectsL = useSpeculativeProjects();
    const trackedProjectsL = useTrackedProjectsInCluster();
    const subscription = useSubscriptionForCluster();
    const isLimitedSub =
        subscription !== "loading" && subscription?.projectLimit != null;
    const effectiveCohort: RankingCohort = isLimitedSub ? "RELATED" : cohort;
    const { scenarioResults } = useScenarioResults();
    // Related projects (when known) flow through to the rank hook as
    // extra allowed project IDs so that on limited subs the returned
    // `projects[]` includes them — without this the cohort filter below
    // would see only tracked/speculative projects and miss legitimate
    // related peers.
    const projects: ScenarioProject[] =
        scenarioResults === "loading" ? [] : scenarioResults.projects;

    // Build a combined projectId -> fuelType map across queue and
    // speculative projects so the "Fuel: ..." cohort doesn't silently
    // drop speculative peers (and the current project on speculative
    // scenario pages) just because they aren't in the queue list.
    const fuelTypeById = useMemo<Map<string, string>>(() => {
        const map = new Map<string, string>();
        if (queueProjectsL !== "loading") {
            for (const p of queueProjectsL) {
                map.set(p.projectId, p.fuelType);
            }
        }
        if (speculativeProjectsL !== "loading") {
            for (const p of speculativeProjectsL) {
                map.set(p.projectId, p.fuelType);
            }
        }
        // Ensure the current project is always resolvable, even if it
        // hasn't loaded from either source yet.
        map.set(currentProject.projectId, currentProject.fuelType);
        return map;
    }, [
        queueProjectsL,
        speculativeProjectsL,
        currentProject.projectId,
        currentProject.fuelType
    ]);

    const currentProjectId = currentProject.projectId;
    const currentFuelType = currentProject.fuelType;
    const portfolioIds = useMemo<Set<string>>(() => {
        if (trackedProjectsL === "loading") return new Set();
        return new Set(
            trackedProjectsL
                .map((p) => p.projectId)
                .filter((id) => id !== currentProjectId)
        );
    }, [trackedProjectsL, currentProjectId]);

    // Related projects come from the scenario-results layer, which already
    // applies the region's `relatedProjectsType` rule (SharedAllocations vs
    // SharedMonitoredFacilities). Falls back to an empty set while results
    // are still loading.
    const relatedIds = useMemo<Set<string>>(() => {
        if (scenarioResults === "loading") return new Set();
        return scenarioResults.relatedProjectIds;
    }, [scenarioResults]);

    const overallDots = useMemo(
        () =>
            buildRankingDots({
                projects,
                assumptions,
                metric,
                cohort: effectiveCohort,
                relatedIds,
                currentProjectId,
                portfolioIds
            }),
        [
            projects,
            assumptions,
            metric,
            effectiveCohort,
            relatedIds,
            currentProjectId,
            portfolioIds
        ]
    );
    const fuelDots = useMemo(
        () =>
            buildRankingDots({
                projects,
                assumptions,
                metric,
                cohort: effectiveCohort,
                relatedIds,
                currentProjectId,
                portfolioIds,
                include: (sp) =>
                    (fuelTypeById.get(sp.projectId) ?? "Other") ===
                    currentFuelType
            }),
        [
            projects,
            assumptions,
            metric,
            effectiveCohort,
            relatedIds,
            fuelTypeById,
            currentProjectId,
            currentFuelType,
            portfolioIds
        ]
    );

    const xFormatter = metric === "perMw" ? formatDollarsPerMw : formatCosts;

    return (
        <div className={css.card}>
            <div className={css.header}>
                <div>
                    <H4 className={chartsCss.chartTitle}>Project ranking</H4>
                    <div className={css.subtitle}>
                        Box plots: min · P25 · median · P75 · max
                    </div>
                </div>
                <div className={css.controls}>
                    {!isLimitedSub && (
                        <SegmentedControl
                            small
                            options={[
                                {
                                    label: "Related projects",
                                    value: "RELATED"
                                },
                                { label: "All projects", value: "ALL" }
                            ]}
                            value={cohort}
                            onValueChange={(v) => {
                                segmentTrackChangeRankingChartCohort({
                                    cohort: v,
                                    isLimitedSub
                                });
                                setCohort(v as RankingCohort);
                            }}
                        />
                    )}
                    <ChartMetricSelect
                        value={
                            metric === "perMw" ? "COST_PER_MW" : "TOTAL_COST"
                        }
                        options={["COST_PER_MW", "TOTAL_COST"]}
                        onChange={(next) => {
                            const value =
                                next === "COST_PER_MW" ? "perMw" : "total";
                            segmentTrackChangeRankingChartMetric({
                                metric: value
                            });
                            setMetric(value);
                        }}
                    />
                </div>
            </div>
            <BoxRow
                label={
                    effectiveCohort === "RELATED"
                        ? "Related projects"
                        : "All projects"
                }
                dots={overallDots}
                xFormatter={xFormatter}
            />
            <BoxRow
                label={
                    effectiveCohort === "RELATED"
                        ? `Fuel: ${currentFuelType} (related)`
                        : `Fuel: ${currentFuelType}`
                }
                dots={fuelDots}
                xFormatter={xFormatter}
            />
            <div className={css.legendRow}>
                <span className={css.legendItem}>
                    <span
                        className={css.legendDot}
                        style={{ background: "#1f7cff" }}
                    />
                    Your project
                </span>
                <span className={css.legendItem}>
                    <span
                        className={css.legendDot}
                        style={{ background: "#f7a23b" }}
                    />
                    Other tracked projects
                </span>
                <span className={css.legendItem}>
                    <span
                        className={css.legendDot}
                        style={{ background: "#a78bfa" }}
                    />
                    Other projects
                </span>
                <Tag
                    minimal
                    intent="primary"
                    style={{
                        visibility:
                            effectiveCohort === "RELATED" ? "visible" : "hidden"
                    }}
                >
                    {isLimitedSub
                        ? "Limited to related projects"
                        : "Filtered to related projects"}
                </Tag>
            </div>
        </div>
    );
};

interface BoxRowProps {
    label: string;
    dots: RankingDot[];
    xFormatter: (n: number) => string;
}

const BOX_WIDTH = 720;
const BOX_HEIGHT = 70;
const PAD_LEFT = 30;
const PAD_RIGHT = 30;
const Y_CENTER = 35;
const BOX_HALF = 16;

type HoveredEntity =
    | { kind: "dot"; dot: RankingDot; leftPx: number }
    | { kind: "outliers"; leftPx: number };

const BoxRow: React.FC<BoxRowProps> = ({ label, dots, xFormatter }) => {
    const stats = useMemo(
        () => fiveNumberSummary(dots.map((d) => d.value)),
        [dots]
    );
    const svgRef = useRef<SVGSVGElement | null>(null);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const [hovered, setHovered] = useState<HoveredEntity | null>(null);

    // Map a point in SVG viewBox coordinates to a pixel offset within
    // the wrapping div. Uses getScreenCTM so it respects the SVG's
    // preserveAspectRatio centering (otherwise the tooltip drifts off
    // the chart area when the wrapper is wider than the rendered SVG).
    const svgXToWrapperPx = (svgX: number): number => {
        const svg = svgRef.current;
        const wrap = wrapRef.current;
        if (!svg || !wrap) return 0;
        const point = svg.createSVGPoint();
        point.x = svgX;
        point.y = 0;
        const matrix = svg.getScreenCTM();
        if (!matrix) return 0;
        const transformed = point.matrixTransform(matrix);
        return transformed.x - wrap.getBoundingClientRect().left;
    };

    if (!stats || dots.length === 0) {
        return (
            <div className={css.boxRow}>
                <div className={css.boxLabel}>
                    {label}
                    <div className={css.boxLabelSub}>0 projects</div>
                </div>
                <div className={css.boxEmpty}>
                    No projects with cost data in this cohort.
                </div>
            </div>
        );
    }

    // X domain clamp: cap at max(P75 * 1.5, P95) so a single huge outlier
    // doesn't squish the IQR box. If max <= clamp, no clamping happens.
    const naturalCap = Math.max(stats.p95, stats.p75 * 1.5);
    const xCap = stats.max > naturalCap ? naturalCap : stats.max;
    const clamped = stats.max > xCap;
    const outliers = dots.filter((d) => d.value > xCap);

    const xScale = (v: number): number => {
        const range = xCap - stats.min || 1;
        const innerW = BOX_WIDTH - PAD_LEFT - PAD_RIGHT;
        const clampedV = Math.min(v, xCap);
        return PAD_LEFT + ((clampedV - stats.min) / range) * innerW;
    };

    const ticks: number[] = [];
    for (let i = 0; i <= 4; i++) {
        ticks.push(stats.min + ((xCap - stats.min) * i) / 4);
    }

    return (
        <div className={css.boxRow}>
            <div className={css.boxLabel}>
                {label}
                <div className={css.boxLabelSub}>
                    {dots.length} project{dots.length === 1 ? "" : "s"}
                    {clamped &&
                        outliers.length > 0 &&
                        ` · ${outliers.length} outlier${
                            outliers.length === 1 ? "" : "s"
                        } ↗`}
                </div>
            </div>
            <div className={css.boxSvgWrap} ref={wrapRef}>
                <svg
                    ref={svgRef}
                    width="100%"
                    viewBox={`0 0 ${BOX_WIDTH} ${BOX_HEIGHT + 20}`}
                    preserveAspectRatio="xMidYMid meet"
                    className={css.boxSvg}
                >
                    {ticks.map((t, i) => (
                        <g key={i}>
                            <line
                                x1={xScale(t)}
                                x2={xScale(t)}
                                y1={Y_CENTER - 28}
                                y2={Y_CENTER + 28}
                                stroke="#eee"
                                strokeDasharray="2 3"
                            />
                            <text
                                x={xScale(t)}
                                y={BOX_HEIGHT + 14}
                                fontSize="10"
                                textAnchor="middle"
                                fill="#888"
                            >
                                {xFormatter(t)}
                            </text>
                        </g>
                    ))}
                    {/* Whiskers (clamped at xCap if max > xCap) */}
                    <line
                        x1={xScale(stats.min)}
                        x2={xScale(stats.p25)}
                        y1={Y_CENTER}
                        y2={Y_CENTER}
                        stroke="#888"
                        strokeWidth={1.5}
                    />
                    <line
                        x1={xScale(stats.p75)}
                        x2={xScale(Math.min(stats.max, xCap))}
                        y1={Y_CENTER}
                        y2={Y_CENTER}
                        stroke="#888"
                        strokeWidth={1.5}
                    />
                    <line
                        x1={xScale(stats.min)}
                        x2={xScale(stats.min)}
                        y1={Y_CENTER - 8}
                        y2={Y_CENTER + 8}
                        stroke="#888"
                        strokeWidth={1.5}
                    />
                    {!clamped && (
                        <line
                            x1={xScale(stats.max)}
                            x2={xScale(stats.max)}
                            y1={Y_CENTER - 8}
                            y2={Y_CENTER + 8}
                            stroke="#888"
                            strokeWidth={1.5}
                        />
                    )}
                    {/* IQR box */}
                    <rect
                        x={xScale(stats.p25)}
                        y={Y_CENTER - BOX_HALF}
                        width={xScale(stats.p75) - xScale(stats.p25)}
                        height={BOX_HALF * 2}
                        fill="#e8eef5"
                        stroke="#9aaec5"
                        strokeWidth={1}
                        rx={2}
                    />
                    {/* Median */}
                    <line
                        x1={xScale(stats.median)}
                        x2={xScale(stats.median)}
                        y1={Y_CENTER - BOX_HALF}
                        y2={Y_CENTER + BOX_HALF}
                        stroke="#2c3e50"
                        strokeWidth={2}
                    />
                    {/* Outlier indicator at the right edge if clamping kicked in */}
                    {clamped && outliers.length > 0 && (
                        <g>
                            <text
                                x={BOX_WIDTH - PAD_RIGHT + 2}
                                y={Y_CENTER + 4}
                                fontSize={14}
                                fill="#888"
                                onMouseEnter={() =>
                                    setHovered({
                                        kind: "outliers",
                                        leftPx: svgXToWrapperPx(
                                            BOX_WIDTH - PAD_RIGHT
                                        )
                                    })
                                }
                                onMouseLeave={() => setHovered(null)}
                            >
                                ↗
                            </text>
                        </g>
                    )}
                    {/* Dots */}
                    {dots.map((d) => (
                        <g key={d.projectId}>
                            {d.isYou && (
                                <>
                                    <line
                                        x1={xScale(d.value)}
                                        y1={Y_CENTER - 10}
                                        x2={xScale(d.value)}
                                        y2={Y_CENTER - 22}
                                        stroke="#1f7cff"
                                        strokeWidth={1}
                                    />
                                    <text
                                        x={xScale(d.value)}
                                        y={Y_CENTER - 26}
                                        textAnchor="middle"
                                        fontSize="11"
                                        fontWeight="700"
                                        fill="#1f7cff"
                                    >
                                        P{d.percentile}
                                    </text>
                                </>
                            )}
                            <circle
                                cx={xScale(d.value)}
                                cy={Y_CENTER}
                                r={d.isYou ? 6 : d.isPortfolio ? 5 : 3}
                                fill={
                                    d.isYou
                                        ? "#1f7cff"
                                        : d.isPortfolio
                                        ? "#f7a23b"
                                        : "#a78bfa"
                                }
                                opacity={
                                    d.isYou ? 1 : d.isPortfolio ? 0.95 : 0.6
                                }
                                stroke={
                                    d.isYou || d.isPortfolio ? "#fff" : "none"
                                }
                                strokeWidth={
                                    d.isYou ? 2 : d.isPortfolio ? 1.5 : 0
                                }
                                pointerEvents="none"
                            />
                            {/* Larger invisible hit target so the tiny r=3
                                dots are reliably hoverable. */}
                            <circle
                                cx={xScale(d.value)}
                                cy={Y_CENTER}
                                r={10}
                                fill="transparent"
                                pointerEvents="all"
                                onMouseEnter={() =>
                                    setHovered({
                                        kind: "dot",
                                        dot: d,
                                        leftPx: svgXToWrapperPx(xScale(d.value))
                                    })
                                }
                                onMouseLeave={() => setHovered(null)}
                            />
                        </g>
                    ))}
                </svg>
                {hovered && (
                    <div
                        className={css.boxTooltip}
                        style={{ left: `${hovered.leftPx}px` }}
                    >
                        {hovered.kind === "dot" ? (
                            <BoxRowDotTooltip
                                dot={hovered.dot}
                                isOutlier={hovered.dot.value > xCap}
                            />
                        ) : (
                            <BoxRowOutliersTooltip
                                outliers={outliers}
                                xFormatter={xFormatter}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const BoxRowDotTooltip: React.FC<{
    dot: RankingDot;
    isOutlier: boolean;
}> = ({ dot, isOutlier }) => (
    <div className={css.tooltip}>
        <div>
            <b>{dot.projectId}</b>
            {dot.isYou ? " — YOU" : dot.isPortfolio ? " — your portfolio" : ""}
        </div>
        <div>Total: {formatCosts(dot.sumAllocatedCost)}</div>
        <div>
            $/MW:{" "}
            {dot.costPerMw !== undefined
                ? formatDollarsPerMw(dot.costPerMw)
                : "—"}
        </div>
        <div>Rank: {formatRank(dot.rank, dot.total, dot.percentile)}</div>
        {isOutlier && (
            <div className={css.tooltipNote}>(outlier — beyond P95)</div>
        )}
    </div>
);

const BoxRowOutliersTooltip: React.FC<{
    outliers: RankingDot[];
    xFormatter: (n: number) => string;
}> = ({ outliers, xFormatter }) => (
    <div className={css.tooltip}>
        <div>
            <b>
                {outliers.length} outlier{outliers.length === 1 ? "" : "s"}{" "}
                beyond P95
            </b>
        </div>
        {outliers.slice(0, 8).map((o) => (
            <div key={o.projectId}>
                {o.projectId}: {xFormatter(o.value)}
            </div>
        ))}
        {outliers.length > 8 && (
            <div className={css.tooltipNote}>
                …and {outliers.length - 8} more
            </div>
        )}
    </div>
);
